import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Trophy, Shield, Target, Award, Lock, Newspaper, Clock, ArrowRight } from "lucide-react";
import useAuthStore from "../game/authStore";
import SEO from "../components/SEO";

// Same categoria labels NoticiasPage.js maps to "Esportes" — the news
// pipeline doesn't have a dedicated football source, but the overwhelming
// majority of "Esportes" articles are football (Brasileirão, Champions,
// World Cup, etc.), so no separate content pipeline is needed here.
const SPORTS_CATEGORIES = ["Esportes", "Futebol", "Basquete", "Tênis", "Esporte"].join(",");

function formatNewsDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
}

// No placeholder-on-error here by design — articles with a broken or
// missing main photo are filtered out of the list entirely (see
// handleNewsImageError below) rather than shown with a placeholder icon.
function NewsThumb({ imageUrl, className = "w-full aspect-video", onError }) {
  return (
    <img src={imageUrl} alt="" onError={onError} loading="lazy" referrerPolicy="no-referrer"
      className={`shrink-0 object-cover rounded-lg ${className}`} />
  );
}

function TeamCrest({ crest, name }) {
  if (crest) {
    return <img src={crest} alt={name} className="w-5 h-5 object-contain shrink-0" />;
  }
  return <Shield size={16} className="text-[var(--muted)] shrink-0" />;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function StandingsTable({ standings }) {
  if (!standings || standings.length === 0) {
    return <p className="text-sm text-[var(--muted)]">Classificação ainda não disponível.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead>
          <tr className="text-[var(--muted)] border-b border-[var(--border)]">
            <th className="text-left py-2 pr-2 font-medium">#</th>
            <th className="text-left py-2 pr-2 font-medium">Time</th>
            <th className="text-center py-2 px-1 font-medium">PJ</th>
            <th className="text-center py-2 px-1 font-medium">V</th>
            <th className="text-center py-2 px-1 font-medium">E</th>
            <th className="text-center py-2 px-1 font-medium">D</th>
            <th className="text-center py-2 px-1 font-medium">SG</th>
            <th className="text-center py-2 pl-1 font-medium">Pts</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s) => (
            <tr key={s.team.id} className="border-b border-[var(--border)] last:border-0">
              <td className="py-2 pr-2 text-[var(--muted)]">{s.position}</td>
              <td className="py-2 pr-2">
                <div className="flex items-center gap-2 min-w-0">
                  <TeamCrest crest={s.team.crest} name={s.team.name} />
                  <span className="text-[var(--text)] truncate">{s.team.shortName || s.team.name}</span>
                </div>
              </td>
              <td className="py-2 px-1 text-center text-[var(--muted)]">{s.played}</td>
              <td className="py-2 px-1 text-center text-[var(--muted)]">{s.won}</td>
              <td className="py-2 px-1 text-center text-[var(--muted)]">{s.draw}</td>
              <td className="py-2 px-1 text-center text-[var(--muted)]">{s.lost}</td>
              <td className="py-2 px-1 text-center text-[var(--muted)]">{s.goalDifference > 0 ? `+${s.goalDifference}` : s.goalDifference}</td>
              <td className="py-2 pl-1 text-center font-bold text-[var(--text)]">{s.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PalpiteRow({ match, isLoggedIn, existingPalpite, onSubmit }) {
  const [home, setHome] = useState(existingPalpite?.palpiteHome ?? "");
  const [away, setAway] = useState(existingPalpite?.palpiteAway ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (home === "" || away === "") return;
    setSaving(true);
    setSaved(false);
    const ok = await onSubmit(match.id, parseInt(home), parseInt(away));
    setSaving(false);
    if (ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
  };

  return (
    <div className="py-3 border-b border-[var(--border)] last:border-0">
      <div className="flex items-center gap-2 mb-1 min-w-0">
        <TeamCrest crest={match.homeTeam.crest} name={match.homeTeam.name} />
        <span className="text-xs sm:text-sm text-[var(--text)] truncate flex-1">{match.homeTeam.shortName || match.homeTeam.name}</span>
      </div>
      <div className="flex items-center gap-2 min-w-0">
        <TeamCrest crest={match.awayTeam.crest} name={match.awayTeam.name} />
        <span className="text-xs sm:text-sm text-[var(--text)] truncate flex-1">{match.awayTeam.shortName || match.awayTeam.name}</span>
      </div>

      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-[9px] text-[var(--muted)] shrink-0">{formatDate(match.utcDate)}</span>

        {isLoggedIn && (
          <form onSubmit={handleSubmit} className="flex items-center gap-1 shrink-0">
            <input type="number" min="0" max="20" value={home} onChange={(e) => setHome(e.target.value)}
              className="w-8 text-center text-xs rounded-md border border-[var(--border)] bg-[var(--card)] text-[var(--text)] py-1" />
            <span className="text-[var(--muted)] text-xs">x</span>
            <input type="number" min="0" max="20" value={away} onChange={(e) => setAway(e.target.value)}
              className="w-8 text-center text-xs rounded-md border border-[var(--border)] bg-[var(--card)] text-[var(--text)] py-1" />
            <button type="submit" disabled={saving || home === "" || away === ""}
              className="ml-1 text-[10px] font-bold px-2 py-1 rounded-md disabled:opacity-40"
              style={{ background: "var(--accent)", color: "#fff" }}>
              {saved ? "✓" : saving ? "..." : "Palpitar"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function FutDoPovoPage() {
  const { user, token } = useAuthStore();
  const [standings, setStandings] = useState(null);
  const [nextMatchday, setNextMatchday] = useState(null);
  const [ranking, setRanking] = useState([]);
  const [meusPalpites, setMeusPalpites] = useState([]);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get("/futdopovo/standings").then((res) => {
      if (res.data?.success) setStandings(res.data.data);
    }).catch(() => {}).finally(() => setLoading(false));

    axios.get("/futdopovo/next-matchday").then((res) => {
      if (res.data?.success) setNextMatchday(res.data.data);
    }).catch(() => {});

    axios.get("/futdopovo/ranking").then((res) => {
      if (res.data?.success) setRanking(res.data.data);
    }).catch(() => {});

    // Fetch a larger pool than shown — articles with no image (or whose
    // image fails to actually load, see handleNewsImageError) get dropped,
    // so the extra buffer keeps the visible list close to 4.
    axios.get("/news", { params: { categorias: SPORTS_CATEGORIES, limit: 10 } }).then((res) => {
      if (res.data?.success) {
        const withPhoto = (res.data.data || []).filter((n) => n.imageUrl);
        setNews(withPhoto.slice(0, 4));
      }
    }).catch(() => {});
  }, []);

  const handleNewsImageError = useCallback((slug) => {
    setNews((prev) => prev.filter((n) => n.slug !== slug));
  }, []);

  useEffect(() => {
    if (!token) { setMeusPalpites([]); return; }
    axios.get("/futdopovo/meus-palpites", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => { if (res.data?.success) setMeusPalpites(res.data.data); })
      .catch(() => {});
  }, [token]);

  const handlePalpite = useCallback(async (matchId, palpiteHome, palpiteAway) => {
    try {
      const res = await axios.post("/futdopovo/palpite",
        { matchId, palpiteHome, palpiteAway },
        { headers: { Authorization: `Bearer ${token}` } });
      return !!res.data?.success;
    } catch {
      return false;
    }
  }, [token]);

  const palpiteMap = {};
  for (const p of meusPalpites) palpiteMap[p.matchId] = p;

  const hasData = standings && standings.length > 0;

  return (
    <div className="max-w-5xl mx-auto">
      <SEO
        title="Fut do Povo - Brasileirão"
        description="Classificação, jogos e palpites do Campeonato Brasileiro Série A, atualizados em tempo real."
        path="/futdopovo"
      />
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Trophy size={26} className="text-[var(--accent)]" />
          <h1 className="text-3xl font-bold text-[var(--text)]">Fut do Povo</h1>
        </div>
        <p className="text-[var(--muted)]">Classificação real do Brasileirão e seus palpites para a próxima rodada</p>
      </div>

      {!loading && !hasData && (
        <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-8 text-center mb-6">
          <Trophy size={32} className="text-[var(--muted)] mx-auto mb-3" />
          <p className="text-[var(--text)] font-medium mb-1">Dados do Brasileirão ainda não sincronizados</p>
          <p className="text-sm text-[var(--muted)]">A classificação e as rodadas aparecem aqui assim que a sincronização rodar.</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Shield size={18} className="text-[var(--accent)]" />
              <h2 className="text-lg font-bold text-[var(--text)]">Classificação — Série A</h2>
            </div>
            <StandingsTable standings={standings} />
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Target size={18} className="text-[var(--accent)]" />
                <h2 className="text-lg font-bold text-[var(--text)]">
                  Palpites {nextMatchday?.matchday ? `— Rodada ${nextMatchday.matchday}` : ""}
                </h2>
              </div>
            </div>

            {!user && (
              <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-[var(--accent-soft)]">
                <Lock size={14} className="text-[var(--accent)] shrink-0" />
                <p className="text-xs text-[var(--muted)]">
                  <Link to="/cleptocracia" className="text-[var(--accent)] font-medium">Crie uma conta</Link> para salvar seus palpites e entrar no ranking.
                </p>
              </div>
            )}

            {nextMatchday?.matches?.length > 0 ? (
              <div>
                {nextMatchday.matches.map((m) => (
                  <PalpiteRow key={m.id} match={m} isLoggedIn={!!user} existingPalpite={palpiteMap[m.id]} onSubmit={handlePalpite} />
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--muted)]">Nenhuma partida agendada no momento.</p>
            )}
          </div>

          <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Award size={18} className="text-[var(--accent)]" />
              <h2 className="text-lg font-bold text-[var(--text)]">Ranking de Palpiteiros</h2>
            </div>
            {ranking.length === 0 ? (
              <p className="text-sm text-[var(--muted)]">Ninguém pontuou ainda — seja o primeiro a palpitar.</p>
            ) : (
              <div className="space-y-1.5">
                {ranking.map((r, i) => (
                  <div key={r.userId} className="flex items-center gap-2 text-sm">
                    <span className="text-[var(--muted)] w-4 text-right shrink-0">{i + 1}</span>
                    <span className="text-[var(--text)] truncate flex-1">{r.displayName}</span>
                    <span className="font-bold text-[var(--accent)] shrink-0">{r.totalPontos} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {news.length > 0 && (
            <div className="bg-[var(--card)] border border-[var(--border)] rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Newspaper size={18} className="text-[var(--accent)]" />
                  <h2 className="text-lg font-bold text-[var(--text)]">Notícias do Futebol</h2>
                </div>
              </div>
              <div className="space-y-3">
                {news.map((n) => (
                  <Link key={n.slug} to={`/noticias/${n.slug}`} className="flex gap-2.5 group">
                    <NewsThumb imageUrl={n.imageUrl} className="w-14 h-14" onError={() => handleNewsImageError(n.slug)} />
                    <div className="min-w-0">
                      <h3 className="text-xs font-semibold text-[var(--text)] group-hover:text-[var(--accent)] transition-colors line-clamp-2">
                        {n.title}
                      </h3>
                      <span className="flex items-center gap-1 text-[10px] text-[var(--muted)] mt-1">
                        <Clock size={10} />
                        {n.dataGeracao ? formatNewsDate(n.dataGeracao) : "Recente"}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
              <Link to="/noticias" className="flex items-center gap-1 text-xs font-medium text-[var(--accent)] mt-3 group">
                Ver mais notícias <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
